import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PENDING, PUBLIC_UNSHARED } from '@careapp-group/core';
import {
  CommentReportsService,
  CommentsService,
  CreateCommentDto,
  CreateCommentReportDto,
  FileRecordDto,
  MemoActionRequestDto,
  MemoRequestDto,
  MemoResponseDto,
  MemosService,
  MemoUpdateRequestDto,
  PublicUrlRequestDto,
} from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  skipWhile,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CAAction } from '../actions';
import { getAuthenticationState } from '../auth/auth.selectors';
import { Feed } from '../feed/feed.actions';
import { getFilesUploading } from '../file-upload/file-upload-selectors';
import * as selectors from '../reducers';
import { CUSTOMERS, getSelectedId, ORGANISATIONS } from '../reducers';
import { Memos } from './memos.actions';

@Injectable()
export class MemosEffects {
  getMemoById = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.REQUEST_BY_ID),
      withLatestFrom(this.store.select(getSelectedId(ORGANISATIONS))),
      mergeMap(([action, org]) =>
        this.memosService.getMemo({ id: action.payload.id }).pipe(
          map((payload: MemoResponseDto) => Memos.ResponseSuccessByIdAction(payload)),
          catchError((err) => of(Memos.ResponseFailAction(err)))
        )
      )
    )
  );

  // Memo a comment creation request to an action that instantly updates the store to avoid comment RTT
  addCommentReceived = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestAddComment),
      withLatestFrom(
        this.store.select(getAuthenticationState),
        this.store.select(selectors.getSelected('memos'))
      ),
      map(([action, auth, memo]) =>
        Memos.instantCreateComment({
          memberId: auth.auth.userId,
          memoId: memo.id,
          body: action.body,
          roles: action.roles,
        })
      )
    )
  );

  instantHeart = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestAddHeartAction, Memos.requestRemoveHeartAction),
      withLatestFrom(this.store.select(getAuthenticationState)),
      map(([action, auth]) =>
        Memos.instantHeart({
          memberId: auth.auth.userId,
          memoId: action.memoId,
          hearted: action.type === Memos.requestAddHeartAction.type,
        })
      )
    )
  );

  addComment = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestAddComment),
      withLatestFrom(
        this.store.select(selectors.getSelected('memos')),
        this.store.select(selectors.org.currentOrgRoles)
      ),
      switchMap(async ([action, memo, roles]) => {
        try {
          const createCommentDto: CreateCommentDto = {
            body: action.body,
          };
          const response = await this.memosService
            .postComment({ id: memo.id, createCommentDto })
            .toPromise();
          return Memos.responseSuccessAddComment({ memo: response });
        } catch (err) {
          return Memos.responseFailAddComment({ error: err, memoId: memo.id });
        }
      })
    )
  );

  addHeart = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestAddHeartAction),
      switchMap((action) => {
        if (action.memoId) {
          return this.memosService.heartMemo({ id: action.memoId }).pipe(
            map((payload) => Memos.responseSuccessAddHeartAction({ memo: payload })),
            catchError((err) =>
              of(Memos.responseFailAddHeartAction({ error: err, memoId: action.memoId }))
            )
          );
        } else {
          return of(
            Memos.responseFailAddHeartAction({
              error: 'Could not heart memo',
              memoId: action.memoId,
            })
          );
        }
      })
    )
  );

  removeHeart = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestRemoveHeartAction),
      switchMap((action) =>
        this.memosService.unheartMemo({ id: action.memoId }).pipe(
          map((payload) => Memos.responseSuccessRemoveHeartAction({ memo: payload })),
          catchError((err) =>
            of(Memos.responseFailRemoveHeartAction({ error: err, memoId: action.memoId }))
          )
        )
      )
    )
  );

  logSavedFile = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestLogSavedFileAction),
      switchMap((action) => {
        if (action.memoId && action.fileId) {
          return this.memosService.logFileSaved({ id: action.memoId, fileId: action.fileId }).pipe(
            map((payload) => Memos.responseSuccessLogSavedFileAction({ memo: payload })),
            catchError((err) =>
              of(Memos.responseFailLogSavedFileAction({ error: err, memoId: action.memoId }))
            )
          );
        } else {
          return of(
            Memos.responseFailLogSavedFileAction({
              error: 'Could not log file as saved.',
              memoId: action.memoId,
            })
          );
        }
      })
    )
  );

  createMemo = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.REQUEST_CREATE),
      switchMap((action: { payload: MemoRequestDto }) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => !!uploading),
          take(1),
          switchMap(() => {
            const originalMemoPayload = action.payload;
            const memoImageIds = originalMemoPayload.fileIds;
            // Memo has no images - we can create it as-is
            if (!memoImageIds || memoImageIds.length === 0) {
              return of(originalMemoPayload);
            }
            // The form gives us server IDs before calling create.
            // Server ID is only available after a file has been uploaded.
            // Safety check that all files have finished uploading before continuing.
            return this.store.select(selectors.getFileUploadState).pipe(
              map((uploadState) => {
                // Check that all the server IDs in memoImageIds exist in the upload state
                const allUploadsComplete = memoImageIds.every((serverId) => {
                  // Find the upload state item that has this server ID as its response
                  const uploadStateItem = Object.values(uploadState).find(
                    (item: { response?: FileRecordDto }) => item?.response?.id === serverId,
                  );
                  return uploadStateItem && uploadStateItem.success;
                });

                return allUploadsComplete;
              }),
              filter((allUploadsComplete) => allUploadsComplete),
              take(1),
              map(() => {
                // Form gives us server IDs
                const payload: MemoRequestDto = {
                  ...originalMemoPayload,
                };
                return payload;
              }),
            );
          }),
        ),
      ),
      withLatestFrom(this.store.select(selectors.getSelected(ORGANISATIONS))),
      switchMap(([payload, organisation]) => {
        return this.memosService
          .postMemo({ memoRequestDto: { ...payload, organisationId: organisation.id } })
          .pipe(
            timeout(environment.httpTimeout),
            map((payload) => Memos.ResponseSuccessCreateAction(payload)),
            tap(() =>
              this.router.navigate([
                '/',
                organisation.tenantId,
                'organisations',
                organisation.id,
                'feed',
              ]),
            ),
            catchError((err) => of(Memos.ResponseFailCreateAction(err))),
          );
      }),
    ),
  );

  editMemo = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.REQUEST_SAVE),
      switchMap((action: { payload: { id: string; data: Partial<MemoRequestDto> } }) =>
        this.store.select(getFilesUploading).pipe(
          skipWhile((uploading) => !!uploading),
          take(1),
          switchMap(() => {
            const updateMemoPayload = action.payload;
            const memoImageIds = updateMemoPayload.data.fileIds;
            // Memo has no images - we can edit it as-is
            if (!memoImageIds || memoImageIds.length === 0) {
              return of(updateMemoPayload);
            }
            // The form gives us server IDs before submitting.
            // Safety check that all files have finished uploading before continuing.
            return this.store.select(selectors.getFileUploadState).pipe(
              map((uploadState) => {
                const newMediaItems = Object.values(uploadState).filter(
                  (item: { response?: FileRecordDto; success?: boolean }) =>
                    item?.response?.id && memoImageIds.includes(item.response.id),
                );

                const allNewUploadsComplete = newMediaItems.every(
                  (item: { success?: boolean }) => item.success,
                );

                return allNewUploadsComplete;
              }),
              filter((allNewUploadsComplete) => allNewUploadsComplete),
              take(1),
              map(() => updateMemoPayload),
            );
          }),
        ),
      ),
      withLatestFrom(this.store.select(selectors.getSelected(ORGANISATIONS))),
      switchMap(([payload, organisation]) => {
        return this.memosService
          .updateMemo({
            id: payload.id,
            memoUpdateRequestDto: {
              categoryIds: payload.data.categoryIds,
              customerIds: payload.data.customerIds,
              fileIds: payload.data.fileIds,
              body: payload.data.body,
            },
          })
          .pipe(
            timeout(environment.httpTimeout),
            map((payload) => Memos.ResponseSuccessSaveAction(payload)),
            tap(() =>
              this.router.navigate([
                '/',
                organisation.tenantId,
                'organisations',
                organisation.id,
                'messages',
                payload.id,
              ]),
            ),
            catchError((err) => of(Memos.ResponseFailSaveAction(err))),
          );
      }),
    ),
  );

  postMemoCreateFeedRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_CREATE),
      map((action) => Feed.RequestAllPaginatedAction(null, 'feed', true))
    )
  );

  createAction = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestCreateActionedAction),
      mergeMap((action) => {
        const memoActionRequestDto: MemoActionRequestDto = {
          type: action.actionType,
          body: action.body,
        };
        return this.memosService.actionMemo({ id: action.memoId, memoActionRequestDto }).pipe(
          map((memo) =>
            Memos.responseSuccessCreateActionedAction({
              memo,
              actionType: action.actionType,
            })
          ),
          catchError((error) =>
            of(
              Memos.responseFailCreateActionedAction({
                actionType: action.actionType,
                memoId: action.memoId,
                error: error,
              })
            )
          )
        );
      })
    )
  );

  retrievePublicUrl = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestPublicUrl),
      switchMap((action) => {
        if (action.memoId) {
          const publicUrlRequestDto: PublicUrlRequestDto = {
            body: action.body,
          };
          return this.memosService.createPublicUrl({ id: action.memoId, publicUrlRequestDto }).pipe(
            map((payload) =>
              Memos.responseSuccessPublicUrl({
                memoId: action.memoId,
                url: payload.url,
              })
            ),
            catchError((err) =>
              of(Memos.responseFailPublicUrl({ error: err, memoId: action.memoId }))
            )
          );
        } else {
          return of(
            Memos.responseFailPublicUrl({
              error: 'Could not retrieve public URL of memo.',
              memoId: action.memoId,
            })
          );
        }
      })
    )
  );

  revokePublicLink = createEffect((): any =>
    this.actions$.pipe(
      ofType(Memos.requestPublicUnshare),
      switchMap((action) => {
        if (action.memoId) {
          const memoActionRequestDto: MemoActionRequestDto = {
            type: PUBLIC_UNSHARED,
          };
          return this.memosService.actionMemo({ id: action.memoId, memoActionRequestDto }).pipe(
            map((payload) =>
              Memos.responseSuccessPublicUnshare({
                memo: payload,
              })
            ),
            catchError((err) =>
              of(Memos.responseFailPublicUnshare({ error: err, memoId: action.memoId }))
            )
          );
        } else {
          return of(
            Memos.responseFailPublicUnshare({
              error: 'Could not revoke public link for memo',
              memoId: action.memoId,
            })
          );
        }
      })
    )
  );

  reportComment = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestReportMemoComment),
      withLatestFrom(this.store.select(getSelectedId(ORGANISATIONS))),
      switchMap(([action, organisationId]) => {
        if (action.commentId && !!action.body.length) {
          return this.commentReportsApi
            .createCommentReport({
              id: action.commentId,
              createCommentReportDto: {
                commentId: action.commentId,
                organisationId: organisationId,
                body: action.body,
              } as CreateCommentReportDto,
            })
            .pipe(
              map((payload) => Memos.responseSuccessReportMemoComment({ report: payload })),
              catchError((err) =>
                of(
                  Memos.responseFailReportMemoComment({
                    commentId: action.commentId,
                    body: action.body,
                    error: err,
                  })
                )
              )
            );
        } else {
          return of(
            Memos.responseFailReportMemoComment({
              commentId: action.commentId,
              body: action.body,
              error: 'Insufficient information provided to report a comment.',
            })
          );
        }
      })
    )
  );

  retrieveCommentReports = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestAllMemoCommentReports),
      withLatestFrom(this.store.select(getSelectedId(ORGANISATIONS))),
      filter(([actions, organisationId]) => !!organisationId),
      switchMap(([action, organisationId]) => {
        return this.commentReportsApi.getCommentReports({ organisationId, status: PENDING }).pipe(
          map((payload) => Memos.responseSuccessAllMemoCommentReports({ reports: payload })),
          catchError((err) =>
            of(
              Memos.responseFailAllMemoCommentReports({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  deleteReportsForComment = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestDeleteReportsForComment),
      switchMap((action) => {
        return this.commentReportsApi.dismissPendingCommentReports({ id: action.commentId }).pipe(
          map((payload) =>
            Memos.responseSuccessDeleteReportsForComment({ commentId: action.commentId })
          ),
          catchError((err) =>
            of(
              Memos.responseFailDeleteReportsForComment({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  deleteComment = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestDeleteMemoComment),
      switchMap((action) => {
        return this.commentsApi.deleteComment({ id: action.commentId }).pipe(
          map((payload) => Memos.responseSuccessDeleteMemoComment({ commentId: action.commentId })),
          catchError((err) =>
            of(
              Memos.responseFailDeleteMemoComment({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  approveComment = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestApproveMemoComment),
      switchMap((action) => {
        return this.commentsApi.approveComment({ id: action.commentId }).pipe(
          map((payload) =>
            Memos.responseSuccessApproveMemoComment({ commentId: action.commentId })
          ),
          catchError((err) =>
            of(
              Memos.responseFailApproveMemoComment({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  requestMemosPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.requestAllPaginatedMemos),
      withLatestFrom(this.store.select(selectors.getSelectedId(CUSTOMERS))),
      switchMap(([action, customerId]) =>
        this.memosService
          .getCustomerMemos({
            customerId,
            skip: action.filter.skip,
            limit: action.filter.limit,
            shared: action.filter.shared,
            hidden: action.filter.hidden,
            type: action.filter.type,
          })
          .pipe(
            map((items) =>
              Memos.responseSuccessAllPaginatedMemos({
                filter: action.filter,
                paginator: action.paginator,
                items: items,
              })
            ),
            catchError((error) =>
              of(Memos.responseFailAllPaginatedMemos({ error, paginator: action.paginator }))
            )
          )
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private memosService: MemosService,
    private commentReportsApi: CommentReportsService,
    private commentsApi: CommentsService,
    private router: Router
  ) {}
}
